"use strict";
/**
 * Domain vocabulary.
 *
 * The symptom, red-flag and amplifier lists are transcribed from
 * docs/Richard_Current_Medications_and_Impairment_Precautions.pdf. They are enums
 * rather than free text because the whole point is to make a reaction QUERYABLE —
 * "show me every episode of excessive sleepiness within six hours of a propranolol
 * dose" is the question the doctors cannot currently ask.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VITALS_ATTENTION = exports.SUBSTANCE_REASONS = exports.AMPLIFYING_SUBSTANCES = exports.SUBSTANCE_KEYS = exports.ONSETS = exports.SEVERITIES = exports.RED_FLAG_SYMPTOMS = exports.SYMPTOMS = exports.CONFIRMATION_STATUSES = exports.SCHEDULE_TYPES = exports.OCCURRENCE_STATUSES = exports.TIME_SOURCES = exports.ACTOR_ROLES = exports.EVENT_TYPES = exports.ALL_PERMISSIONS = exports.PERMISSIONS = void 0;
exports.isRedFlag = isRedFlag;
exports.hasRedFlag = hasRedFlag;
exports.isAmplifier = isAmplifier;
exports.vitalsWorthMentioning = vitalsWorthMentioning;
exports.PERMISSIONS = [
    'VIEW_LOG',
    'LOG_ON_BEHALF',
    'MANAGE_REGIMEN',
    'MANAGE_CARE_TEAM',
    'RECEIVE_ALERTS',
    'EXPORT_REPORT',
];
exports.ALL_PERMISSIONS = exports.PERMISSIONS;
exports.EVENT_TYPES = ['DOSE_TAKEN', 'SYMPTOM', 'EXTRA_SUBSTANCE', 'VITALS'];
exports.ACTOR_ROLES = ['SELF', 'CAREGIVER'];
/**
 * How the recorded time was arrived at. Clinically load-bearing: a doctor needs to
 * know which times are real-time observation and which are recall.
 */
exports.TIME_SOURCES = ['NOW', 'ADJUSTED', 'BACKFILLED'];
exports.OCCURRENCE_STATUSES = [
    'OPEN',
    'REMINDED',
    'TAKEN',
    'MISSED',
    'SKIPPED',
    /** Denise's rule, enforced by data: a slot this med does not have must render as a
     *  blocked-out cell so it cannot be mistaken for a missed dose. */
    'NOT_SCHEDULED',
];
exports.SCHEDULE_TYPES = [
    'FIXED',
    'AS_NEEDED',
    /** Prescribed frequency genuinely unknown. Generates no occurrences and therefore no
     *  missed-dose alerts — a false alarm on a PRN drug trains everyone to ignore alerts. */
    'AS_NEEDED_UNCONFIRMED',
];
/** Provenance of the schedule itself. Surfaced in the UI and footnoted in the report. */
exports.CONFIRMATION_STATUSES = ['CONFIRMED', 'PROBABLE', 'UNCONFIRMED'];
// ---------------------------------------------------------------------------
// Symptoms
// ---------------------------------------------------------------------------
/** Impairment signs named in the precautions document. */
exports.SYMPTOMS = [
    'EXCESSIVE_SLEEPINESS',
    'SLEPT_MUCH_MORE_THAN_USUAL',
    'FATIGUE',
    'LETHARGY',
    'DIZZY',
    'LIGHTHEADED',
    'WEAK',
    'UNSTEADY',
    'SLOWED_THINKING',
    'DISORIENTED',
    'SHORT_TERM_MEMORY_DIFFICULTY',
    'FAINTNESS',
    'NAUSEATED',
    'VOMITING',
    'HEADACHE',
    'HEART_RACING',
    'HEART_SLOW',
    'OTHER',
];
/**
 * "Treat it as a possible medical emergency rather than assuming it is medication.
 *  Call 911." — transcribed verbatim in meaning from the precautions document.
 *
 * Selecting any of these triggers a full-screen 911 interstitial and an immediate
 * caregiver alert that bypasses quiet hours and coalescing. The symptom is saved
 * regardless of what the user taps next.
 */
exports.RED_FLAG_SYMPTOMS = [
    'SLURRED_SPEECH',
    'CANNOT_WALK_NORMALLY',
    'MARKED_CONFUSION',
    'ONE_SIDED_WEAKNESS',
    'FACIAL_DROOPING',
    'FAINTING',
    'SEVERE_DIFFICULTY_STAYING_AWAKE',
    'CHEST_PAIN',
    'TROUBLE_BREATHING',
    'SEIZURE',
    'UNAROUSABLE',
];
const RED_FLAG_SET = new Set(exports.RED_FLAG_SYMPTOMS);
function isRedFlag(symptom) {
    return RED_FLAG_SET.has(symptom);
}
function hasRedFlag(symptoms) {
    return symptoms.some(isRedFlag);
}
exports.SEVERITIES = ['MILD', 'MODERATE', 'BAD'];
exports.ONSETS = ['NOW', 'WITHIN_LAST_HOUR', 'EARLIER_TODAY', 'LAST_NIGHT'];
// ---------------------------------------------------------------------------
// Extra substances
// ---------------------------------------------------------------------------
/**
 * "Alcohol, cannabis, sleep medications, sedating antihistamines, benzodiazepines,
 *  opioids, gabapentin, muscle relaxants and other medications may add to dizziness
 *  or sedation. A complete medication and substance list is therefore important."
 */
exports.SUBSTANCE_KEYS = [
    'ANTACID',
    'IBUPROFEN',
    'ACETAMINOPHEN',
    'ASPIRIN',
    'SEDATING_ANTIHISTAMINE',
    'SLEEP_MED',
    'OPIOID',
    'MUSCLE_RELAXANT',
    'BENZODIAZEPINE',
    'GABAPENTIN',
    'ALCOHOL',
    'CANNABIS',
    'SUPPLEMENT',
    'OTHER_RX',
    'OTC_OTHER',
];
/**
 * Substances that amplify impairment. Flagged separately because this is precisely the
 * correlation a doctor needs, and precisely what a patient is least likely to volunteer
 * in a visit.
 */
exports.AMPLIFYING_SUBSTANCES = [
    'SEDATING_ANTIHISTAMINE',
    'SLEEP_MED',
    'OPIOID',
    'MUSCLE_RELAXANT',
    'BENZODIAZEPINE',
    'GABAPENTIN',
    'ALCOHOL',
    'CANNABIS',
];
const AMPLIFIER_SET = new Set(exports.AMPLIFYING_SUBSTANCES);
function isAmplifier(substanceKey) {
    return AMPLIFIER_SET.has(substanceKey);
}
exports.SUBSTANCE_REASONS = [
    'HEADACHE',
    'HEARTBURN',
    'PAIN',
    'COULD_NOT_SLEEP',
    'NAUSEA',
    'ANXIETY',
    'OTHER',
];
// ---------------------------------------------------------------------------
// Vitals thresholds
// ---------------------------------------------------------------------------
/**
 * "A very slow pulse or unusually low blood pressure may accompany excessive
 *  propranolol effects."
 *
 * These flag a reading as worth mentioning to a clinician. They are NOT advice, NOT a
 * diagnosis, and never gate an entry.
 */
exports.VITALS_ATTENTION = {
    pulseBpmLow: 50,
    pulseBpmHigh: 120,
    systolicLow: 90,
    diastolicLow: 60,
};
/** Returns the reasons a reading is worth telling the doctor about. Empty = nothing flagged. */
function vitalsWorthMentioning(v) {
    const flags = [];
    if (v.pulseBpm !== undefined && v.pulseBpm < exports.VITALS_ATTENTION.pulseBpmLow) {
        flags.push('PULSE_LOW');
    }
    if (v.pulseBpm !== undefined && v.pulseBpm > exports.VITALS_ATTENTION.pulseBpmHigh) {
        flags.push('PULSE_HIGH');
    }
    if (v.systolic !== undefined && v.systolic < exports.VITALS_ATTENTION.systolicLow) {
        flags.push('SYSTOLIC_LOW');
    }
    if (v.diastolic !== undefined && v.diastolic < exports.VITALS_ATTENTION.diastolicLow) {
        flags.push('DIASTOLIC_LOW');
    }
    return flags;
}
//# sourceMappingURL=domain.js.map